import React from 'react'
import { useSelector } from 'react-redux'

import { type SchemaContact } from '@/__generated/es-api'
import { useConfirmation } from '@/app/contexts/confirmation.context'
import { usePaginated } from '@/app/hooks/use-paginated'
import { useSearchable } from '@/app/hooks/use-searchable'
import { useGetContactsWithSubscriptions } from '@/app/module/campaigns/hooks/use-get-contacts-with-subscriptions'
import { useReopenSub } from '@/app/module/campaigns/hooks/use-reopen-sub'
import { useSubscribeContact } from '@/app/module/campaigns/hooks/use-subscribe-contact'
import { useSubscribeContacts } from '@/app/module/campaigns/hooks/use-subscribe-contacts'
import { useUnsubscribeContacts } from '@/app/module/campaigns/hooks/use-unsubscribe-contacts'
import { type CampaignItem } from '@/app/module/campaigns/types'
import { SubscriptionFilter, type SubscriptionQuery } from '@/app/module/campaigns/types/subscriptions'
import { toDelay } from '@/app/module/campaigns/utils/delay'
import { processContactLabel } from '@/app/module/cati/helpers'
import { selectOrgId } from '@/app/module/user/store/selectors'
import { type CountryTimezone, type Timezones } from '@/app/types'
import config from '@/config'

import DelayDialogContent, { type DelaySubscriptionPayload } from './delay-dialog'
import { canResume, getSubscriptionStatus } from './helpers'
import { ResponsiveContactSubTable } from './responsive-sub-tables'

type Props = {
  campaign: CampaignItem
  campaignId: number
  countriesTimezones: CountryTimezone[]
  countryCode: string
  pageSearchTerm?: string // search params in URL
  timezone: string
  timezones: Timezones
}

const ContactSubscriptionsTable: React.FC<Props> = (props) => {
  const [subscribeContact, { isLoading: isSubscribing }] = useSubscribeContact()
  const [subscribeContacts, { isLoading: isBulkSubscribing }] = useSubscribeContacts()
  const [unsubscribeContacts, { isLoading: isUnsubscribing }] = useUnsubscribeContacts()
  const [reopenSubscription, { isLoading: isReopening }] = useReopenSub()
  const orgId = useSelector(selectOrgId)

  const { campaign, campaignId, countriesTimezones, countryCode, pageSearchTerm = '', timezone, timezones } = props
  const [query, setQuery] = React.useState({
    ...defaultQuery,
    subscriptionStatus: campaignId.toString(),
  })
  const createConfirmation = useConfirmation()

  const { data: contactsResponse, isFetching: loading, refetch } = useGetContactsWithSubscriptions(campaignId, query)

  const data = React.useMemo(() => contactsResponse?.contacts || [], [contactsResponse])
  const total = contactsResponse?.total || 0
  const [selectedContactIds, setSelectedContactIds] = React.useState<number[]>([])
  const [selectedContactsById, setSelectedContactsById] = React.useState<Record<number, SchemaContact>>({})
  const selectedContactData = React.useMemo(
    () => selectedContactIds.map((contactId) => selectedContactsById[contactId]).filter((contact) => !!contact),
    [selectedContactIds, selectedContactsById],
  )
  const resumableSubIds = React.useMemo(() => {
    if (!canResume(campaign.type)) {
      return []
    }

    return selectedContactData
      .filter((contact) => getSubscriptionStatus(contact.subscription) === 'done' && !!contact.subscription?.lastId)
      .map((contact) => contact.subscription!.lastId!)
  }, [campaign.type, selectedContactData])
  const reset = React.useCallback(
    () =>
      setQuery({
        ...defaultQuery,
        subscriptionStatus: campaignId.toString(),
      }),
    [campaignId],
  )

  const handleGetData = React.useCallback(async (q: SubscriptionQuery) => setQuery(q), [])
  const setPageSize = React.useCallback((pageSize: number) => {
    setQuery((s) => ({
      ...s,
      page: 1,
      size: pageSize,
    }))
  }, [])

  const pagination = usePaginated({
    query,
    root: 'contacts',
    total,
    getData: handleGetData,
    navigate: setQuery,
    setPageSize,
  })

  const search = useSearchable<SubscriptionQuery>({
    orgId,
    pageSearchTerm,
    query,
    getData: handleGetData,
    navigate: setQuery,
    reset,
  })

  const handleSubscriptionFilterChange = React.useCallback(
    (filter: SubscriptionFilter) =>
      setQuery((s) => ({
        ...s,
        subscriptionFilter: filter,
      })),
    [],
  )

  const getSubscribeTitle = React.useCallback(
    (contactIds: number[]) => {
      if (contactIds.length > 1) {
        return <>Subscribe {contactIds.length} Contacts</>
      }

      let name = ''

      const contact = selectedContactsById[contactIds[0]] || data.find((item) => item.id === contactIds[0])
      if (contact) {
        if (contact.firstName || contact.lastName || contact.fullPhoneNumber) {
          name = `${processContactLabel(contact)}${contact.fullPhoneNumber ? ` (${contact.fullPhoneNumber})` : ''}`
        } else {
          name = `Contact ID ${contact.id} (Contact details hidden)`
        }
      }
      return <>Subscribe {name} </>
    },
    [data, selectedContactsById],
  )

  const clearSelection = React.useCallback(() => {
    setSelectedContactIds([])
    setSelectedContactsById({})
  }, [])

  const handleSelectContact = React.useCallback(
    (contactId: number, checked: boolean) => {
      setSelectedContactIds((current) => {
        if (!checked) {
          return current.filter((id) => id !== contactId)
        }

        return current.includes(contactId) ? current : [...current, contactId]
      })
      setSelectedContactsById((current) => {
        if (!checked) {
          const { [contactId]: _removedContact, ...nextSelected } = current
          return nextSelected
        }

        const contact = data.find((item) => item.id === contactId)
        return contact ? { ...current, [contactId]: contact } : current
      })
    },
    [data],
  )

  const handleSubscribe = React.useCallback(
    async (contactIds: number[]) => {
      if (contactIds.length === 0) {
        return
      }

      let subscriptionPayload: DelaySubscriptionPayload = {}

      const confirmed = await createConfirmation({
        title: getSubscribeTitle(contactIds),
        content: ({ setConfirmButtonDisabled }) => (
          <DelayDialogContent
            campaign={campaign}
            countriesTimezones={countriesTimezones}
            countryCode={countryCode}
            timezone={timezone}
            timezones={timezones}
            onChange={(payload) => {
              subscriptionPayload = payload
            }}
            setConfirmButtonDisabled={setConfirmButtonDisabled}
          />
        ),
        confirmButtonLabel: 'Confirm',
        cancelButtonLabel: 'Cancel',
        icon: 'input',
        fullWidth: true,
        maxWidth: 'sm',
      })

      if (confirmed) {
        if (contactIds.length === 1) {
          await subscribeContact(campaignId, {
            contactId: contactIds[0],
            delay: subscriptionPayload.delay && toDelay(subscriptionPayload.delay),
            startAt: subscriptionPayload.startAt,
            t0: subscriptionPayload.t0,
            t0timezone: subscriptionPayload.t0timezone,
          })
        } else {
          await subscribeContacts(campaignId, {
            allowResubscribe: true,
            args: {
              startAt: subscriptionPayload.startAt,
              t0: subscriptionPayload.t0,
              t0timezone: subscriptionPayload.t0timezone,
            },
            contacts: {
              includedContactIds: contactIds,
            },
            delay: subscriptionPayload.delay && toDelay(subscriptionPayload.delay),
          })
        }
        clearSelection()
      }
    },
    [
      campaign,
      campaignId,
      countriesTimezones,
      countryCode,
      timezone,
      timezones,
      clearSelection,
      createConfirmation,
      getSubscribeTitle,
      subscribeContact,
      subscribeContacts,
    ],
  )

  const handleUnsubscribe = React.useCallback(
    async (contactIds: number[]) => {
      if (contactIds.length === 0) {
        return
      }

      await unsubscribeContacts(campaignId, {
        includedContactIds: contactIds,
      })
      clearSelection()
    },
    [campaignId, clearSelection, unsubscribeContacts],
  )

  const handleResume = React.useCallback(
    async (subscriptionId: number) => {
      await reopenSubscription(subscriptionId)
      clearSelection()
    },
    [clearSelection, reopenSubscription],
  )

  const isLoading = isReopening || isSubscribing || isBulkSubscribing || isUnsubscribing || loading
  const selectionDisabledReason = getSelectionDisabledReason({
    campaignOpen: campaign.open,
    campaignPublished: campaign.published,
    isLoading,
  })

  return (
    <ResponsiveContactSubTable
      campaign={campaign}
      contacts={data}
      isLoading={isLoading}
      pagination={pagination}
      refreshDisabled={loading}
      resumableSubIds={resumableSubIds}
      searchTerm={search.searchTerm}
      selectedContactIds={selectedContactIds}
      selectedContactsById={selectedContactsById}
      selectionDisabledReason={selectionDisabledReason}
      subscriptionFilter={query.subscriptionFilter}
      onClearSelection={clearSelection}
      onRefresh={() => refetch()}
      onResume={handleResume}
      onSearch={search.onSubmit}
      onSelectContact={handleSelectContact}
      onSubscribe={handleSubscribe}
      onSubscriptionFilterChange={handleSubscriptionFilterChange}
      onUnsubscribe={handleUnsubscribe}
    />
  )
}

const defaultQuery: Omit<SubscriptionQuery, 'subscriptionStatus'> = {
  page: 1,
  size: config.modules.contacts.pageSize.default,
  searchTerm: '',
  subscriptionFilter: SubscriptionFilter.All,
}

const getLoadingDisabledReason = (isLoading: boolean) =>
  isLoading ? 'Please wait until the current subscription action is finished.' : ''

const getSelectionDisabledReason = ({
  campaignOpen,
  campaignPublished,
  isLoading,
}: {
  campaignOpen: boolean
  campaignPublished: boolean
  isLoading: boolean
}) => {
  if (!campaignOpen && !campaignPublished) {
    return 'Campaign has to be opened to be able to select contacts.'
  }

  return getLoadingDisabledReason(isLoading)
}

export default ContactSubscriptionsTable
